"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.NO_RESPONSE_STATUS_CODE = exports.FormattedAxiosError = void 0;
exports.formatError = formatError;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _axios = require("@ori/axios");
var _fetchErrors = require("@ori/fetch-errors");
var _esToolkit = require("es-toolkit");
var _errors = require("../errors");
const MAX_BODY_LENGTH = 200;
/**
 * Represents a status code of `0` to indicate that no response was received from the server.
 *
 * @remarks
 * This constant is used in scenarios where the HTTP request did not reach the server or the server
 * did not provide a valid response, such as when the network is unavailable, the request times out,
 * or there's a CORS error. Since `0` is not a valid HTTP status code, it serves as a special
 * placeholder to signal the absence of any server response.
 * @deprecated Use `NO_RESPONSE_STATUS_CODE` from `@ori/fetch-errors` instead.
 */
const NO_RESPONSE_STATUS_CODE = exports.NO_RESPONSE_STATUS_CODE = 0;

/**
 * This formats AxiosError into more readable object not only for user but also for Application Insights
 * @deprecated Use `NewFormattedAxiosError` instead.
 */
class FormattedAxiosError extends Error {
  constructor(axiosError) {
    super(`Axios Error: ${axiosError.message}`);
    (0, _defineProperty2.default)(this, "baseUrl", void 0);
    (0, _defineProperty2.default)(this, "pathname", void 0);
    (0, _defineProperty2.default)(this, "method", void 0);
    (0, _defineProperty2.default)(this, "statusCode", void 0);
    (0, _defineProperty2.default)(this, "statusText", void 0);
    (0, _defineProperty2.default)(this, "responseBody", null);
    (0, _defineProperty2.default)(this, "innerCallstack", void 0);
    this.name = 'FormattedAxiosError';
    const {
      response,
      stack,
      code,
      config,
      status
    } = axiosError;
    // Response could be undefined on timeout for example
    this.baseUrl = response?.config.baseURL ?? config?.baseURL ?? null;
    this.pathname = response?.config.url ?? config?.url ?? null;
    this.method = (response?.config.method ?? config?.method)?.toUpperCase() ?? null;
    this.statusCode = response?.status ?? status ?? NO_RESPONSE_STATUS_CODE;
    this.statusText = response?.statusText ?? code ?? null;

    // No need to pass body on 404 and empty body
    if (this.statusCode !== 404 && response?.data !== undefined && response.data !== null) {
      if (process.env.NODE_ENV === 'production') {
        const dataAsJson = JSON.stringify(response.data);
        const numberOfChars = dataAsJson.length;
        // Make sure that body is trimmed so we wont overfill Application Insights
        this.responseBody = dataAsJson.slice(0, MAX_BODY_LENGTH);
        if (numberOfChars > MAX_BODY_LENGTH) {
          // And still show how many chars are actually there
          this.responseBody += `... (+${numberOfChars - MAX_BODY_LENGTH})`;
        }
      } else {
        this.responseBody = JSON.stringify(response.data, null, 2);
      }
    }
    this.innerCallstack = stack ?? null;
  }
}

/**
 * Formats the given error to ensure consistent error handling.
 *
 * If the error is an Axios error, it is wrapped in a FormattedAxiosError.
 * The function recursively formats any `cause` property that is also an error.
 *
 * @param providedError - The error object to be formatted. Can be any unknown type.
 * @returns A properly formatted error object.
 */
exports.FormattedAxiosError = FormattedAxiosError;
function formatError(providedError) {
  if (providedError instanceof _errors.FormattedAxiosError) {
    return providedError;
  }
  if (!(typeof providedError === 'object') || !(providedError instanceof Error)) {
    return new Error(providedError.toString());
  }
  let error;
  // Check if the error is an AxiosError and format it
  if ((0, _axios.isAxiosError)(providedError)) {
    error = new _errors.FormattedAxiosError(new _fetchErrors.ExtendedAxiosError(providedError));
  } else if (providedError instanceof _fetchErrors.GraphqlError) {
    error = new _errors.FormattedGraphqlError(providedError);
  } else {
    error = (0, _esToolkit.cloneDeep)(providedError);
  }

  // If the error has a cause, recursively format it
  if (providedError.cause instanceof Error) {
    error.cause = formatError(providedError.cause);
  }
  return error;
}
